import React, { useState, useEffect } from 'react';
import { ChevronRight, Calendar, User, ExternalLink, FileText, TrendingUp, Globe, Shield, Zap, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom'; // Ensure Link is imported for BlogSection

// Placeholder for BlogSection. In a real application, you would import your actual BlogSection component.
// For this example, we'll create a simple inline placeholder.
const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleStart, setVisibleStart] = useState(0);
  const visibleCount = 3; // Number of blogs visible at once

  // Define the API URL using process.env
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      try {
        const response = await fetch(`${API_URL}/blog`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setBlogs(data);
          } else {
            setError("No blogs found in the database.");
            setBlogs([]);
          }
        } else {
          throw new Error(`Failed to fetch blogs. Status: ${response.status}`);
        }
      } catch (err) {
        console.error("Error fetching blogs from backend:", err);
        setError("Connection to threat intelligence database failed. Please check your connection.");
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [API_URL]);

  // Scroll functionality for blog cards
  const scroll = (direction) => {
    if (direction === 'left') {
      setVisibleStart((prev) => Math.max(0, prev - 1));
    } else {
      setVisibleStart((prev) => Math.min(blogs.length - visibleCount, prev + 1));
    }
  };

  // Get color based on threat level
  const getThreatColor = (threat) => {
    switch (threat?.toUpperCase()) {
      case 'CRITICAL': return 'text-red-600';
      case 'HIGH': return 'text-orange-600';
      case 'MEDIUM': return 'text-yellow-600';
      case 'LOW': return 'text-green-600';
      default: return 'text-[#0ea5e9]'; // Default to a blue tone
    }
  };

  // Get background color based on threat level
  const getThreatBg = (threat) => {
    switch (threat?.toUpperCase()) {
      case 'CRITICAL': return 'bg-red-100 border-red-200';
      case 'HIGH': return 'bg-orange-100 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 border-yellow-200';
      case 'LOW': return 'bg-green-100 border-green-200';
      default: return 'bg-[#eff6ff] border-[#bfdbfe]'; // Default to light blue
    }
  };

  return (
    <section className="relative min-h-screen bg-white text-[#1f2937] font-inter overflow-hidden">
      {/* Animated Background Elements - Replicated from MediaAndResources */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid-pattern-light"></div> {/* Changed to light version */}
        </div>

        {/* Gradient Orbs */}
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] rounded-full flex items-center justify-center animate-pulse-glow-blue">
                <Shield className="w-10 h-10 text-white animate-rotate-slow" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
                <span className="text-xs font-bold text-white">!</span>
              </div>
              {/* Orbiting dots - Adjusted colors */}
              <div className="absolute inset-0 animate-spin-slow">
                <div className="absolute -top-4 left-1/2 w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse"></div>
                <div className="absolute -right-4 top-1/2 w-2 h-2 bg-[#3b82f6] rounded-full animate-pulse animation-delay-500"></div>
                <div className="absolute -bottom-4 left-1/2 w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse animation-delay-1000"></div>
                <div className="absolute -left-4 top-1/2 w-2 h-2 bg-[#3b82f6] rounded-full animate-pulse animation-delay-1500"></div>
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl lg:text-6xl font-black mb-4 leading-tight">
            <span className="bg-gradient-to-r from-[#1e40af] via-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent animate-gradient-text">
              GLOCYBS
            </span>
            <br />
            <span className="text-[#1f2937]">CYBER INTELLIGENCE</span>
          </h1>
          
          <p className="text-lg text-[#6b7280] mb-8 max-w-2xl mx-auto">
            Stay ahead of evolving threats with our latest 
            <span className="text-[#0ea5e9] font-bold animate-pulse"> security insights</span> and 
            <span className="text-[#3b82f6] font-bold animate-pulse"> threat intelligence</span>
          </p>
          
          {/* Animated Status Bar */}
          <div className="max-w-md mx-auto bg-[#f8fafc] backdrop-blur-md rounded-full border border-[#e2e8f0] p-2 shadow-sm">
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">LIVE FEED ACTIVE</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#64748b]">THREAT LEVEL:</span>
                <span className="text-xs text-orange-500 font-bold">ELEVATED</span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-3">
              <div className="w-8 h-8 border-4 border-[#0ea5e9] border-t-transparent rounded-full animate-spin"></div>
              <span className="text-[#0ea5e9] font-semibold">ACCESSING THREAT DATABASE...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center animate-fade-in">
            <div className="flex items-center justify-center gap-2">
              <svg className="w-6 h-6 text-red-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-600 font-semibold">{error}</span>
            </div>
          </div>
        )}

        {/* Blog Navigation */}
        {!loading && blogs.length > 0 && (
          <div className="flex items-center justify-between mb-8 animate-fade-in">
            <button
              onClick={() => scroll('left')}
              disabled={visibleStart === 0}
              className={`group px-6 py-3 rounded-lg border transition-all duration-300 ${
                visibleStart === 0 
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white hover:shadow-lg transform hover:scale-105'
              }`}
            >
              <div className="flex items-center gap-2">
                <ChevronRight className="w-5 h-5 transform rotate-180 transition-transform group-hover:-translate-x-1" /> {/* Adjusted icon */}
                <span className="font-medium">PREVIOUS</span>
              </div>
            </button>
            
            <div className="flex items-center gap-4 bg-[#f8fafc] rounded-full px-6 py-3 border border-[#e2e8f0]">
              <span className="text-[#64748b] font-semibold text-sm">SHOWING</span>
              <span className="text-[#1f2937] font-bold">{visibleStart + 1}-{Math.min(visibleStart + visibleCount, blogs.length)}</span>
              <span className="text-[#94a3b8]">OF</span>
              <span className="text-[#1f2937] font-bold">{blogs.length}</span>
            </div>
            
            <button
              onClick={() => scroll('right')}
              disabled={visibleStart >= blogs.length - visibleCount}
              className={`group px-6 py-3 rounded-lg border transition-all duration-300 ${
                visibleStart >= blogs.length - visibleCount
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white hover:shadow-lg transform hover:scale-105'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">NEXT</span>
                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          </div>
        )}

        {/* Blog Grid */}
        {!loading && blogs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {blogs.slice(visibleStart, visibleStart + visibleCount).map((blog, index) => (
              <div 
                key={blog.id || blog._id} 
                className="group bg-white rounded-3xl border border-[#e2e8f0] overflow-hidden hover:border-[#0ea5e9] transition-all duration-500 transform hover:scale-105 hover:shadow-2xl animate-fade-in-up cursor-pointer"
                style={{animationDelay: `${index * 200}ms`}}
              >
                
                {/* Blog Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={blog.iconUrl || blog.image || 'https://placehold.co/400x250/e0f2fe/1e40af?text=Blog+Image'} 
                    alt={blog.heading || blog.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div> {/* Lighter overlay */}
                  
                  {/* Threat Level Badge */}
                  {blog.threat_level && (
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full border ${getThreatBg(blog.threat_level)} animate-pulse`}>
                      <span className={`text-xs font-bold ${getThreatColor(blog.threat_level)}`}>
                        {blog.threat_level}
                      </span>
                    </div>
                  )}
                  
                  {/* Overlay Animation */}
                  <div className="absolute inset-0 bg-[#0ea5e9]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Blog Content */}
                <div className="p-6">
                  {/* Category Badge */}
                  {blog.category && (
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#eff6ff] border border-[#bfdbfe] rounded-full mb-4">
                      <div className="w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse"></div>
                      <span className="text-xs text-[#1e40af] font-medium">{blog.category}</span>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold text-[#1f2937] mb-3 leading-tight group-hover:text-[#0ea5e9] transition-colors duration-300">
                    {blog.heading || blog.title}
                  </h3>
                  
                  <p className="text-[#6b7280] text-sm mb-4 line-clamp-3 group-hover:text-[#374151] transition-colors duration-300">
                    {blog.content || blog.description}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-xs text-[#94a3b8] mb-4">
                    <span>{blog.readTime || '5 min read'}</span>
                    <span>{blog.author || 'GLOCYBS Team'}</span>
                  </div>

                  {/* Read More Button */}
                  <Link 
                    to={`/blog/${blog.id || blog._id}`}
                    className="group/btn inline-flex items-center gap-2 text-[#0ea5e9] hover:text-[#1e40af] transition-all duration-300 font-semibold"
                  >
                    <span>READ MORE</span>
                    <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No blogs message */}
        {!loading && blogs.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 animate-fade-in">
              <div className="flex items-center justify-center gap-2 mb-4">
                <svg className="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-yellow-600 text-xl font-bold">NO INTELLIGENCE REPORTS FOUND</span>
              </div>
              <p className="text-yellow-500 text-lg">
                No threat intelligence reports are currently available. Please check back later or contact our team for more information.
              </p>
            </div>
          </div>
        )}

        {/* View All Button */}
        {!loading && blogs.length > 0 && (
          <div className="text-center animate-fade-in">
            <Link 
              to="/blogs"
              className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:from-[#0284c7] hover:to-[#2563eb]"
            >
              <Globe className="w-5 h-5 transition-transform group-hover:rotate-12" />
              ACCESS ALL INTELLIGENCE
              <div className="w-2 h-2 bg-white/50 rounded-full animate-pulse"></div>
            </Link>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        /* Import Inter font from Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(40px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        @keyframes float-reverse {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-180deg); }
        }

        @keyframes pulse-glow-blue { /* Specific for blue theme */
          0%, 100% { 
            box-shadow: 0 0 20px rgba(14, 165, 233, 0.3),
                        0 0 40px rgba(14, 165, 233, 0.1); 
          }
          50% { 
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.5),
                        0 0 80px rgba(14, 165, 233, 0.2); 
          }
        }

        @keyframes gradient-text {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-gradient-text {
          background-size: 200% 200%;
          animation: gradient-text 3s ease-in-out infinite;
        }

        /* Floating Shapes - Replicated */
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(59, 130, 246, 0.1));
          animation: float 6s ease-in-out infinite;
        }

        .shape-1 {
          width: 100px;
          height: 100px;
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 10%;
          animation: float-reverse 8s ease-in-out infinite;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 80px;
          height: 80px;
          top: 30%;
          right: 30%;
          animation-delay: 4s;
        }

        .shape-4 {
          width: 120px;
          height: 120px;
          bottom: 20%;
          left: 20%;
          animation: float-reverse 7s ease-in-out infinite;
          animation-delay: 1s;
        }

        /* Grid Pattern - Replicated with light colors */
        .grid-pattern-light {
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: float 20s linear infinite; /* Using existing float for subtle movement */
        }

        /* Gradient Orbs - Replicated */
        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
          animation: float 10s ease-in-out infinite;
        }

        .orb-1 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(6, 182, 212, 0.15));
          top: 20%;
          left: -10%;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.15));
          bottom: 20%;
          right: -10%;
          animation-delay: 5s;
        }

        .orb-3 {
          width: 200px;
          height: 200px;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(14, 165, 233, 0.15));
          top: 50%;
          left: 50%;
          animation-delay: 3s;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-4 { /* Added for consistency, though not explicitly used in BlogSection's current P tag */
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Removed old dark theme specific animations like scan-horizontal, scan-vertical, main-content-glow */
      `}</style>
    </section>
  );
};

// The main MediaAndResources component remains unchanged, just ensuring BlogSection is correctly imported.
const MediaAndResources = () => {
  const [news, setNews] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('blog');
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/news`);
        if (!response.ok) throw new Error('Failed to fetch news');
        const newsData = await response.json();
        setNews(newsData);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load news');
      }
    };

    const fetchReports = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/report`);
        if (!response.ok) throw new Error('Failed to fetch reports');
        const reportsData = await response.json();
        setReports(reportsData);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
    fetchReports();
  }, [API_BASE_URL]);

  const handleReportClick = (reportId) => {
    navigate('/report', { state: { reportId } });
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Quantum Security': <Shield className="w-5 h-5" />,
      'IoT Security': <Zap className="w-5 h-5" />,
      'Cloud Security': <Globe className="w-5 h-5" />,
      'AI Security': <Brain className="w-5 h-5" />,
      'Threat Intelligence': <TrendingUp className="w-5 h-5" />,
      'Research': <FileText className="w-5 h-5" />
    };
    return iconMap[category] || <FileText className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <p className="mt-4 text-[#1e40af] font-semibold">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden font-inter">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid-pattern-light"></div> {/* Ensure this matches BlogSection */}
        </div>

        {/* Gradient Orbs */}
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg">
            <Globe className="w-4 h-4 mr-2" />
            GLOCYBS Media Center
          </div>
          
          <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-[#1e40af] via-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent animate-gradient-text">
              Media &
            </span>
            <br />
            <span className="text-[#1f2937]">Resources</span>
          </h1>
          
          <p className="text-xl text-[#6b7280] mb-8 max-w-3xl mx-auto leading-relaxed">
            Discover cutting-edge insights from GLOCYBS experts on quantum security, AI-powered threat detection, 
            and the evolving cybersecurity landscape with BIQUA, G-FLEA, and QUBISA technologies.
          </p>

          {/* Tab Navigation */}
          <div className="inline-flex bg-[#f8fafc] rounded-2xl p-2 shadow-lg border border-[#e2e8f0] mb-12">
            <button
              onClick={() => setActiveTab('blog')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'blog'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Blog Posts
            </button>
            <button
              onClick={() => setActiveTab('news')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'news'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Latest News
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === 'reports'
                  ? 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white shadow-lg transform scale-105'
                  : 'text-[#64748b] hover:text-[#1e40af] hover:bg-white'
              }`}
            >
              Research Reports
            </button>
          </div>
        </div>

        {/* Blog Section Content */}
        {activeTab === 'blog' && (
          <div className="animate-fade-in-up">
            <BlogSection />
          </div>
        )}

        {/* Error Handling Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <p className="text-red-600 font-semibold">{error}</p>
          </div>
        )}

        {/* News Section Content */}
        {activeTab === 'news' && (
          <div className="animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {news.length > 0 ? news.map((item, index) => (
                <div
                  key={item._id || `news-${index}`}
                  className="group bg-white rounded-3xl border border-[#e2e8f0] overflow-hidden hover:border-[#0ea5e9] transition-all duration-500 transform hover:scale-105 hover:shadow-2xl cursor-pointer news-card"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] rounded-xl text-white">
                        {getCategoryIcon(item.category)}
                      </div>
                      <div>
                        <span className="text-xs text-[#64748b] font-medium uppercase tracking-wide">
                          {item.category || 'News'}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
                          <Calendar className="w-3 h-3" />
                          {new Date(item.date || item.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-[#1f2937] mb-4 leading-tight group-hover:text-[#0ea5e9] transition-colors duration-300">
                      {item.title}
                    </h3>
                    
                    <p className="text-[#6b7280] text-sm mb-6 line-clamp-3 leading-relaxed">
                      {item.description || item.content}
                    </p>
                    
                    <a
                      href={item.link || '#'}
                      className="inline-flex items-center gap-2 text-[#0ea5e9] hover:text-[#1e40af] transition-all duration-300 font-semibold group/btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>Read More</span>
                      <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </a>
                  </div>
                  
                  <div className="h-1 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              )) : (
                <div className="col-span-full text-center py-16">
                  <div className="text-[#94a3b8] text-lg">No news articles available at the moment.</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Section Content */}
        {activeTab === 'reports' && (
          <div className="animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
              {reports.length > 0 ? reports.map((report, index) => (
                <div
                  key={report._id || `report-${index}`}
                  className="group bg-gradient-to-br from-white to-[#f8fafc] rounded-3xl border border-[#e2e8f0] overflow-hidden hover:border-[#0ea5e9]
                    transition-all duration-300 hover:shadow-2xl report-card"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="flex flex-col flex-grow p-6 md:p-8 h-full">
                    {/* Card header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="p-3 bg-gradient-to-r from-[#06b6d4] to-[#0ea5e9] rounded-2xl text-white flex-shrink-0">
                        <FileText className="w-5 h-5 md:w-6 md:h-6" />
                      </div>
                      <span className="bg-[#eff6ff] text-[#1e40af] px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                        RESEARCH
                      </span>
                    </div>
                    
                    {/* Card content */}
                    <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-[#1f2937] mb-4 leading-tight group-hover:text-[#0ea5e9] transition-colors duration-300">
                      {report.title}
                    </h3>
                    
                    <div className="flex-grow">
                      <p className="text-[#6b7280] mb-6 leading-relaxed text-sm md:text-base">
                        {report.description || report.summary || report.content}
                      </p>
                    </div>
                    
                    {/* Card footer */}
                    <div className="flex items-center justify-between gap-4 mt-auto">
                      <div className="flex items-center gap-2 text-sm text-[#64748b]">
                        <User className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{report.author || 'GLOCYBS Research Team'}</span>
                      </div>
                      <div
                        className="inline-flex items-center cursor-pointer flex-shrink-0"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleReportClick(report._id || report.id);
                        }}
                      >
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6]
                          text-white px-4 md:px-6 py-2 md:py-3 rounded-xl hover:shadow-lg transition-all duration-300
                          font-semibold hover:from-[#0284c7] hover:to-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9]
                          focus:ring-offset-2 text-sm md:text-base"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View Report</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-16">
                  {loading ? (
                    <div className="loading-spinner">
                      <div className="spinner-ring"></div>
                      <p className="mt-4 text-[#1e40af] font-semibold">Loading reports...</p>
                    </div>
                  ) : (
                    <div className="text-[#94a3b8] text-lg">No research reports available at the moment.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        /* Import Inter font from Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(40px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        @keyframes float-reverse {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-180deg); }
        }

        @keyframes pulse-glow-blue { /* Specific for blue theme */
          0%, 100% { 
            box-shadow: 0 0 20px rgba(14, 165, 233, 0.3),
                        0 0 40px rgba(14, 165, 233, 0.1); 
          }
          50% { 
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.5),
                        0 0 80px rgba(14, 165, 233, 0.2); 
          }
        }

        @keyframes gradient-text {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-gradient-text {
          background-size: 200% 200%;
          animation: gradient-text 3s ease-in-out infinite;
        }

        /* Floating Shapes - Replicated */
        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(59, 130, 246, 0.1));
          animation: float 6s ease-in-out infinite;
        }

        .shape-1 {
          width: 100px;
          height: 100px;
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 10%;
          animation: float-reverse 8s ease-in-out infinite;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 80px;
          height: 80px;
          top: 30%;
          right: 30%;
          animation-delay: 4s;
        }

        .shape-4 {
          width: 120px;
          height: 120px;
          bottom: 20%;
          left: 20%;
          animation: float-reverse 7s ease-in-out infinite;
          animation-delay: 1s;
        }

        /* Grid Pattern - Replicated with light colors */
        .grid-pattern-light {
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: float 20s linear infinite; /* Using existing float for subtle movement */
        }

        /* Gradient Orbs - Replicated */
        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
          animation: float 10s ease-in-out infinite;
        }

        .orb-1 {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(6, 182, 212, 0.15));
          top: 20%;
          left: -10%;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(147, 51, 234, 0.15));
          bottom: 20%;
          right: -10%;
          animation-delay: 5s;
        }

        .orb-3 {
          width: 200px;
          height: 200px;
          background: linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(14, 165, 233, 0.15));
          top: 50%;
          left: 50%;
          animation-delay: 3s;
        }

        .news-card {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
          position: relative;
          z-index: 1;
        }

        .report-card {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 390px;
        }

        .news-card:hover {
          animation: pulse-glow-blue 2s ease-in-out infinite; /* Changed to blue pulse */
          transform: scale(1.05);
        }

        .report-card:hover {
          box-shadow: 0 20px 25px -5px rgba(14, 165, 233, 0.1), 0 10px 10px -5px rgba(14, 165, 233, 0.04);
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @media (min-width: 1024px) {
          .report-card {
            min-height: auto;
          }
          .report-card p {
            -webkit-line-clamp: unset; 
            height: auto; 
          }
        }

        .loading-spinner {
          text-align: center;
        }

        .spinner-ring {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(14, 165, 233, 0.2);
          border-top: 4px solid #0ea5e9;
          border-radius: 50%;
          animation: spin-slow 1s linear infinite;
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
};

export default MediaAndResources;
